/**
 * F8 / Reqs 10+12 — helpers de cards bruto/neto y orden de detalle de cierre.
 * Neto de medio = ingresos − egresos del medio (sin comisiones/liquidación).
 *
 * Totales generales (definición de negocio F8):
 * - Total neto  = saldo inicial + efectivo neto
 * - Total bruto = total neto + resto de medios ARS (cheques + electrónico)
 * El campo BE `total_neto` ≈ total bruto (incluye todos los medios); no usarlo como “neto”.
 */

export type CierreCardAmounts = {
  saldoInicial: number;
  efectivoIngresos: number;
  efectivoGastos: number;
  chequesIngresos: number;
  chequesGastos: number;
  tarjetaCreditoIngresos: number;
  tarjetaDebitoIngresos: number;
  transferenciaIngresos: number;
  tarjetaCreditoGastos: number;
  tarjetaDebitoGastos: number;
  transferenciaGastos: number;
  totalFacturas: number;
  dolaresIngresos: number;
  dolaresGastos: number;
  saldoInicialUsd: number;
  /** Campo BE `total_neto` (amplio); preferir totalNetoCaja / totalBrutoCaja para UI. */
  totalNeto: number;
};

function num(...candidates: unknown[]): number {
  for (const c of candidates) {
    if (c === null || c === undefined || c === "") continue;
    const n = Number(c);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

/** Lee buckets snake/camel del payload API. */
export function readCierreCardAmounts(cierre: Record<string, unknown>): CierreCardAmounts {
  return {
    saldoInicial: num(cierre.saldo_inicial, cierre.saldoInicial),
    efectivoIngresos: num(cierre.efectivo_ingresos, cierre.efectivoIngresos),
    efectivoGastos: num(cierre.efectivo_gastos, cierre.efectivoGastos),
    chequesIngresos: num(cierre.cheques_ingresos, cierre.chequesIngresos),
    chequesGastos: num(cierre.cheques_gastos, cierre.chequesGastos),
    tarjetaCreditoIngresos: num(
      cierre.tarjeta_credito_ingresos,
      cierre.tarjetaCreditoIngresos,
    ),
    tarjetaDebitoIngresos: num(
      cierre.tarjeta_debito_ingresos,
      cierre.tarjetaDebitoIngresos,
    ),
    transferenciaIngresos: num(
      cierre.transferencia_ingresos,
      cierre.transferenciaIngresos,
    ),
    tarjetaCreditoGastos: num(
      cierre.tarjeta_credito_gastos,
      cierre.tarjetaCreditoGastos,
    ),
    tarjetaDebitoGastos: num(
      cierre.tarjeta_debito_gastos,
      cierre.tarjetaDebitoGastos,
    ),
    // Bug histórico: Electrónico restaba transferenciaIngresos; usar solo gastos.
    transferenciaGastos: num(
      cierre.transferencia_gastos,
      cierre.transferenciaGastos,
    ),
    totalFacturas: num(cierre.total_facturas, cierre.totalFacturas),
    dolaresIngresos: num(cierre.dolares_ingresos, cierre.dolaresIngresos),
    dolaresGastos: num(cierre.dolares_gastos, cierre.dolaresGastos),
    saldoInicialUsd: num(cierre.saldo_inicial_usd, cierre.saldoInicialUsd),
    totalNeto: num(cierre.total_neto, cierre.totalNeto),
  };
}

/** Suma de ingresos electrónicos (tarjeta + débito + transferencia). Legacy F8. */
export function electronicoBruto(a: CierreCardAmounts): number {
  return (
    a.tarjetaCreditoIngresos + a.tarjetaDebitoIngresos + a.transferenciaIngresos
  );
}

export function electronicoGastos(a: CierreCardAmounts): number {
  return a.tarjetaCreditoGastos + a.tarjetaDebitoGastos + a.transferenciaGastos;
}

/**
 * Electrónico para UI/totales = ingresos − egresos del medio (igual que efectivo/cheques).
 * F3b: una sola card «Electrónico»; bruto/neto duplicados eran naming F8 (no IVA/comisiones).
 */
export function electronicoNeto(a: CierreCardAmounts): number {
  return electronicoBruto(a) - electronicoGastos(a);
}

/** Alias UI F3b — mismo valor que electronicoNeto. */
export function electronico(a: CierreCardAmounts): number {
  return electronicoNeto(a);
}

export function efectivoNeto(a: CierreCardAmounts): number {
  return a.efectivoIngresos - a.efectivoGastos;
}

export function chequesNeto(a: CierreCardAmounts): number {
  return a.chequesIngresos - a.chequesGastos;
}

/** Total neto de caja = saldo inicial + efectivo neto (misma semántica que arrastre F11). */
export function totalNetoCaja(a: CierreCardAmounts): number {
  return a.saldoInicial + efectivoNeto(a);
}

/** Saldo caja USD = SI_usd + D del día − H del día. No entra al neto ARS. */
export function dolaresCajaSaldo(a: CierreCardAmounts): number {
  return a.saldoInicialUsd + a.dolaresIngresos - a.dolaresGastos;
}

export type CierreDolaresCardView = {
  amount: number;
  /** Subtítulo bajo Dólares. Siempre null: «SI USD {formatUsd}» salía «SI USD USD 0.00». */
  caption: string | null;
};

/** Vista de la card Dólares (Cierre Diario + Histórico). Sin leftover SI USD. */
export function cierreDolaresCardView(a: CierreCardAmounts): CierreDolaresCardView {
  return {
    amount: dolaresCajaSaldo(a),
    caption: null,
  };
}

/**
 * Total bruto = total neto + resto de medios ARS (cheques + electrónico).
 * No incluye Cta Cte ni dólares físicos; sin comisiones inventadas.
 */
export function totalBrutoCaja(a: CierreCardAmounts): number {
  return totalNetoCaja(a) + chequesNeto(a) + electronicoNeto(a);
}

export type DetalleSortKey =
  | "descripcion"
  | "referencia"
  | "tipo"
  | "metodo"
  | "debe"
  | "haber";

const TIPO_RANK: Record<string, number> = {
  factura: 10,
  nota_credito: 20,
  cobro: 30,
  cobro_usd: 40,
  movimiento_caja: 50,
  fondo: 60,
  pago_proveedor: 70,
};

/** Línea de NC a saldo a favor: movimiento real, sin Debe/Haber ni ingreso/egreso. */
export function detalleSinImporte(mov: {
  origenKey?: string | null;
  origen_key?: string | null;
}): boolean {
  const key = String(mov.origenKey ?? mov.origen_key ?? "");
  return key.endsWith(":sin_importe");
}

export function defaultDetalleSortKey(detalle: {
  tipo?: string;
  origenKey?: string;
  origen_key?: string;
  id?: number;
}): [number, string, string, number] {
  const tipo = detalle.tipo || "";
  const origen = detalle.origenKey || detalle.origen_key || "";
  return [TIPO_RANK[tipo] ?? 99, tipo, origen, detalle.id ?? 0];
}

export function sortCierreDetalles<T extends {
  tipo?: string;
  origenKey?: string;
  origen_key?: string;
  id?: number;
  descripcion?: string | null;
  referencia?: string | null;
  metodoPago?: string | null;
  metodo_pago?: string | null;
  esIngreso?: boolean;
  es_ingreso?: boolean;
  importe?: number | string;
  importeArs?: number | string;
  importe_ars?: number | string;
}>(
  detalles: T[],
  sortKey: DetalleSortKey | "default" = "default",
  dir: "asc" | "desc" = "asc",
): T[] {
  const mul = dir === "desc" ? -1 : 1;
  const copy = [...detalles];
  copy.sort((a, b) => {
    if (sortKey === "default") {
      const ka = defaultDetalleSortKey(a);
      const kb = defaultDetalleSortKey(b);
      for (let i = 0; i < ka.length; i++) {
        if (ka[i] < kb[i]) return -1 * mul;
        if (ka[i] > kb[i]) return 1 * mul;
      }
      return 0;
    }
    const esIngresoA = a.esIngreso ?? a.es_ingreso ?? true;
    const esIngresoB = b.esIngreso ?? b.es_ingreso ?? true;
    const debeA = esIngresoA ? num(a.importeArs, a.importe_ars, a.importe) : 0;
    const debeB = esIngresoB ? num(b.importeArs, b.importe_ars, b.importe) : 0;
    const haberA = !esIngresoA ? num(a.importe) : 0;
    const haberB = !esIngresoB ? num(b.importe) : 0;
    const metodoA = (a.metodoPago || a.metodo_pago || "").toString();
    const metodoB = (b.metodoPago || b.metodo_pago || "").toString();
    let cmp = 0;
    switch (sortKey) {
      case "descripcion":
        cmp = (a.descripcion || "").localeCompare(b.descripcion || "", "es");
        break;
      case "referencia":
        cmp = (a.referencia || "").localeCompare(b.referencia || "", "es");
        break;
      case "tipo":
        cmp = (a.tipo || "").localeCompare(b.tipo || "", "es");
        break;
      case "metodo":
        cmp = metodoA.localeCompare(metodoB, "es");
        break;
      case "debe":
        cmp = debeA - debeB;
        break;
      case "haber":
        cmp = haberA - haberB;
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp * mul;
    // Tie-break estable
    const ka = defaultDetalleSortKey(a);
    const kb = defaultDetalleSortKey(b);
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  });
  return copy;
}
